import type { NonEmptyArray, Optional } from '@festina-life/core'

import type { TreeLeafData, TreeNodeData } from './types'

export const addLeafToTree = <Leaf extends TreeLeafData>({
  tree,
  leaf,
  atPath,
  currentPath = [],
  nameFromPathPart = (part) => part,
}: {
  tree: TreeNodeData<Leaf>[]
  leaf: Leaf
  atPath: string
  currentPath?: string[]
  nameFromPathPart?: (part: string) => string
}) => {
  const [nextPathPart, ...rest] = atPath.split('.')

  const targetPath = [...currentPath, nextPathPart]
  const targetPathAsString = targetPath.join('.')
  let targetNode = tree.find(
    (node) => node.path.join('.') === targetPathAsString,
  )

  if (!targetNode) {
    targetNode = {
      path: targetPath as NonEmptyArray<string>,
      name: nameFromPathPart(nextPathPart),
      leaves: [],
      nodes: [],
      tags: [],
    }

    tree.push(targetNode)
  }

  const doesLeafBelongToTargetNode = !rest.length

  if (doesLeafBelongToTargetNode) {
    targetNode.leaves.push(leaf)
  } else {
    addLeafToTree({
      tree: targetNode.nodes,
      leaf,
      atPath: rest.join('.'),
      currentPath: [...currentPath, nextPathPart],
      nameFromPathPart,
    })
  }
  targetNode.tags = mergeTags([...targetNode.tags, ...leaf.tags])
}

export const collectAllPaths = <Leaf extends TreeLeafData>(
  tree: TreeNodeData<Leaf>[],
): string[] =>
  tree.flatMap((treeNode) => [
    treeNode.path.join('.'),
    ...collectAllPaths(treeNode.nodes),
  ])

export const getParentTreeNode = <Leaf extends TreeLeafData>({
  tree,
  path,
  nameFromPathPart,
}: {
  tree: TreeNodeData<Leaf>[]
  path: Optional<string[]>
  nameFromPathPart: (part: string) => string
}): Optional<TreeNodeData<Leaf>> => {
  if (!path?.length || !tree.length) {
    return
  }

  const [first, ...rest] = path

  const node = tree.find(
    (treeNode) => treeNode.name === nameFromPathPart(first),
  )

  if (!node) {
    return
  }

  return rest.length
    ? getParentTreeNode({ tree: node.nodes, path: rest, nameFromPathPart })
    : node
}

const mergeTags = (tags: string[]) => Array.from(new Set(tags))

export const pruneTreeWithQuery = <T extends TreeLeafData>(
  nodes: TreeNodeData<T>[],
  query: Optional<string>,
): TreeNodeData<T>[] => {
  if (!query) {
    return nodes
  }

  return pruneTreeWithLowerQuery(nodes, query.toLocaleLowerCase())
}

const pruneTreeWithLowerQuery = <T extends TreeLeafData>(
  nodes: TreeNodeData<T>[],
  lowerQuery: string,
): TreeNodeData<T>[] =>
  nodes
    .map((node) => {
      const filteredLeaves = node.leaves.filter((leaf) =>
        leaf.id.toLocaleLowerCase().includes(lowerQuery),
      )
      const prunedChildNodes = pruneTreeWithLowerQuery(node.nodes, lowerQuery)

      return { ...node, nodes: prunedChildNodes, leaves: filteredLeaves }
    })
    .filter(
      (node) =>
        node.name?.toLocaleLowerCase().includes(lowerQuery) ||
        !!node.nodes.length ||
        !!node.leaves.length,
    )
